/**
 * The two screens in front of the dashboard: sign-in, and the forced password change that follows
 * a first sign-in with an admin-issued password.
 */

import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { changePassword, login, logout } from "../api";
const MIN_LENGTH = 10;

export function LoginForm(): JSX.Element {
  const queryClient = useQueryClient();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(username, password);
      // /api/me now reports the session, including whether a password change is due.
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Sign-in failed.");
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <form className="login-card login-form" onSubmit={submit}>
        <div className="rail-mark">P</div>
        <h1>PrismSMP Staff Portal</h1>
        <p>Sign in with the username and password an admin gave you.</p>
        <input
          className="input"
          placeholder="Username"
          autoComplete="username"
          autoFocus
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          aria-label="Username"
        />
        <input
          className="input"
          type="password"
          placeholder="Password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          aria-label="Password"
        />
        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}
        <button className="btn" data-variant="primary" type="submit" disabled={busy || !username || !password}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

export function ChangePasswordForm({
  username,
  csrfToken,
}: {
  username: string;
  csrfToken: string;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (next.length < MIN_LENGTH) return setError(`Use at least ${MIN_LENGTH} characters.`);
    if (next !== confirm) return setError("The two new passwords do not match.");
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, next, csrfToken);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not change the password.");
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <form className="login-card login-form" onSubmit={submit}>
        <div className="rail-mark">P</div>
        <h1>Choose a new password</h1>
        <p>
          Welcome, <b>{username}</b>. You signed in with a temporary password from an admin — set your
          own before continuing.
        </p>
        <input
          className="input"
          type="password"
          placeholder="Temporary password"
          autoComplete="current-password"
          autoFocus
          value={current}
          onChange={(event) => setCurrent(event.target.value)}
          aria-label="Temporary password"
        />
        <input
          className="input"
          type="password"
          placeholder={`New password (${MIN_LENGTH}+ characters)`}
          autoComplete="new-password"
          value={next}
          onChange={(event) => setNext(event.target.value)}
          aria-label="New password"
        />
        <input
          className="input"
          type="password"
          placeholder="New password again"
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          aria-label="Confirm new password"
        />
        {error && (
          <div className="login-error" role="alert">
            {error}
          </div>
        )}
        <button className="btn" data-variant="primary" type="submit" disabled={busy || !current || !next || !confirm}>
          {busy ? "Saving…" : "Set password"}
        </button>
        <button
          className="btn"
          type="button"
          onClick={async () => {
            await logout(csrfToken);
            window.location.reload();
          }}
        >
          Sign out
        </button>
      </form>
    </div>
  );
}
